import type { Vector3 } from "three";
import { COBWEB, isSolid, WATER } from "../world/blocks";
import { WORLD_HEIGHT } from "../world/chunk";
import type { BlockView } from "./dropped-items";

/**
 * Shared voxel physics for mobs.
 *
 * Passive animals and hostile mobs have different brains but must obey the
 * same collision rules. Keeping the AABB sweep here means a fix to stepping,
 * ledges or water affects every mob consistently instead of drifting apart in
 * two copies.
 */

export type MobWorld = BlockView;

export interface MobBody {
  readonly position: Vector3;
  readonly velocity: Vector3;
  onGround: boolean;
  blocked: boolean;
}

export const MOB_GRAVITY = 28;
export const MOB_JUMP_SPEED = 8.4;
export const MOB_WATER_JUMP_SPEED = 10.5;
export const MOB_TERMINAL_VELOCITY = 40;
export const MOB_WATER_BUOYANCY = 28;
export const MOB_WATER_RISE_SPEED = 1.4;
export const MOB_MAX_STEP = 0.05;
export const MOB_MAX_SUBSTEP = 0.22;

const EPS = 1e-4;

/** True while the chunk containing this world column is loaded. */
export function isMobLoaded(world: MobWorld, x: number, z: number): boolean {
  if (!world.isChunkLoaded) return true;
  return world.isChunkLoaded(Math.floor(x), Math.floor(z));
}

/** Does this voxel block mob movement? Unloaded space is treated as solid. */
export function mobBlockSolid(world: MobWorld, wx: number, wy: number, wz: number): boolean {
  if (wy < 0) return true;
  if (wy >= WORLD_HEIGHT) return false;
  if (!isMobLoaded(world, wx, wz)) return true;
  return isSolid(world.peekBlock(wx, wy, wz));
}

/**
 * Check if the mob overlaps a water block and determine the top water surface level.
 * Returns null if the mob is not in water.
 */
export function mobWaterInfo(
  world: MobWorld,
  pos: Vector3,
  height: number,
): { surfaceY: number } | null {
  const x = Math.floor(pos.x);
  const z = Math.floor(pos.z);
  const feetY = Math.floor(pos.y + 0.05);
  const midY = Math.floor(pos.y + height * 0.5);

  const feetBlock = world.peekBlock(x, feetY, z);
  const midBlock = world.peekBlock(x, midY, z);
  const blockBelow = world.peekBlock(x, Math.floor(pos.y - 0.1), z);

  if (feetBlock !== WATER && midBlock !== WATER && blockBelow !== WATER) {
    return null;
  }

  // Find the top-most water block in this column around the mob
  let topWaterY = Math.max(feetY, midY);
  while (topWaterY < WORLD_HEIGHT && world.peekBlock(x, topWaterY + 1, z) === WATER) {
    topWaterY++;
  }
  while (topWaterY >= 0 && world.peekBlock(x, topWaterY, z) !== WATER) {
    topWaterY--;
  }
  if (topWaterY < 0) return null;

  // Visual water surface is topWaterY + 0.88
  const surfaceY = topWaterY + 0.88;
  if (pos.y >= surfaceY + 0.05) return null;

  return { surfaceY };
}

/** Does the mob's AABB overlap a solid voxel? */
export function mobCollides(
  world: MobWorld,
  pos: Vector3,
  width: number,
  height: number,
): boolean {
  const half = width / 2;
  const minX = Math.floor(pos.x - half + EPS);
  const maxX = Math.floor(pos.x + half - EPS);
  const minY = Math.floor(pos.y + EPS);
  const maxY = Math.floor(pos.y + height - EPS);
  const minZ = Math.floor(pos.z - half + EPS);
  const maxZ = Math.floor(pos.z + half - EPS);

  for (let y = minY; y <= maxY; y++) {
    for (let z = minZ; z <= maxZ; z++) {
      for (let x = minX; x <= maxX; x++) {
        if (mobBlockSolid(world, x, y, z)) return true;
      }
    }
  }
  return false;
}

/** Does the mob's AABB overlap a specific block id (e.g. Cobweb)? */
export function mobOverlapsBlock(
  world: MobWorld,
  pos: Vector3,
  width: number,
  height: number,
  blockId: number,
): boolean {
  const half = width / 2;
  const minX = Math.floor(pos.x - half + EPS);
  const maxX = Math.floor(pos.x + half - EPS);
  const minY = Math.floor(pos.y + EPS);
  const maxY = Math.floor(pos.y + height - EPS);
  const minZ = Math.floor(pos.z - half + EPS);
  const maxZ = Math.floor(pos.z + half - EPS);

  for (let y = minY; y <= maxY; y++) {
    if (y < 0 || y >= WORLD_HEIGHT) continue;
    for (let z = minZ; z <= maxZ; z++) {
      for (let x = minX; x <= maxX; x++) {
        if (world.peekBlock(x, y, z) === blockId) return true;
      }
    }
  }
  return false;
}

/**
 * Move one body by its velocity for `dt`, one axis at a time, snapping flush
 * against solid voxels. Resets `onGround` and `blocked` for this step.
 */
export function moveMob(
  world: MobWorld,
  body: MobBody,
  dt: number,
  width: number,
  height: number,
  immuneToWeb = false,
): void {
  body.onGround = false;
  body.blocked = false;

  if (!immuneToWeb && mobOverlapsBlock(world, body.position, width, height, COBWEB)) {
    body.velocity.x *= 0.25;
    body.velocity.z *= 0.25;
    if (body.velocity.y < 0) body.velocity.y *= 0.05;
  }

  moveMobAxis(world, body, 0, body.velocity.x * dt, width, height);
  moveMobAxis(world, body, 1, body.velocity.y * dt, width, height);
  moveMobAxis(world, body, 2, body.velocity.z * dt, width, height);
}

function moveMobAxis(
  world: MobWorld,
  body: MobBody,
  axis: 0 | 1 | 2,
  delta: number,
  width: number,
  height: number,
): void {
  if (delta === 0) return;
  const half = width / 2;
  const back = axis === 1 ? 0 : half;
  const fwd = axis === 1 ? height : half;
  const steps = Math.max(1, Math.ceil(Math.abs(delta) / MOB_MAX_SUBSTEP));
  const inc = delta / steps;
  const key = axis === 0 ? "x" : axis === 1 ? "y" : "z";

  for (let s = 0; s < steps; s++) {
    body.position[key] += inc;
    if (!mobCollides(world, body.position, width, height)) continue;

    const lo = body.position[key] - back;
    const hi = body.position[key] + fwd;
    if (inc > 0) {
      body.position[key] = Math.floor(hi) - fwd - EPS;
    } else {
      body.position[key] = Math.floor(lo) + 1 + back + EPS;
      if (axis === 1) body.onGround = true;
    }

    if (axis === 1) {
      body.velocity.y = 0;
    } else {
      body.velocity[key === "x" ? "x" : "z"] = 0;
      body.blocked = true;
    }
  }
}
